import { Button } from "@/components/ui/button";
import { FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/store";
import { safeZodResolver, onFormValidationError } from "@/utils/safe-zod-resolver";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { PasswordInput } from "./password-input";
import { withReturnUrl } from "@/utils/return-url";
import { withInvitationId } from "@/utils/invitation-url";

// Validation schema for registration form
const registerSchema = z
  .object({
    name: z
      .string()
      .min(1, "Name is required")
      .min(2, "Name must be at least 2 characters"),
    email: z
      .string()
      .min(1, "Email is required")
      .email("Please enter a valid email address")
      .refine((email) => !email.includes("+"), {
        message: 'Email aliases with "+" are not allowed',
      }),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(
        /[^A-Za-z0-9]/,
        "Password must contain at least one special character"
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export interface RegisterFormProps {
  prefilledEmail?: string
  onSuccess?: () => void;
}

/**
 * Registration form component with name, email, password, and confirm password validation
 * Features real-time validation, loading states, and integration with auth context
 */
export function RegisterForm({ onSuccess, prefilledEmail }: RegisterFormProps) {
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const { register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const invitationId = searchParams.get('invitationId')

  const form = useForm<RegisterFormData>({
    resolver: safeZodResolver(registerSchema as any) as any,
    defaultValues: {
      name: "",
      email: prefilledEmail || "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await register(data.name, data.email, data.password, invitationId ?? undefined);
      console.log('Registration result:', result); // Debug log for registration result

      if (result?.redirectUrl?.includes('/verify-otp')) {
        // Still needs OTP verification — preserve invitationId through the
        // flow so InvitationHandler can accept it once the user verifies.
        let redirectUrl = withInvitationId(result.redirectUrl, invitationId)
        // Preserve the post-auth destination too, so a user who signed up from
        // a deep-link still lands there after verifying their email.
        redirectUrl = withReturnUrl(redirectUrl, searchParams);
        navigate(redirectUrl, { replace: true });
      } else if (result?.redirectUrl) {
        // Signup returned an authenticated session directly — a validated
        // invitation let it skip OTP and the workspace was already attached
        // server-side, so no further invitation handling is needed here.
        navigate(result.redirectUrl, { replace: true });
      } else {
        onSuccess?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="text-left mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">
          Create your account
        </h1>
        <p className="text-sm text-muted-foreground">
          Enter your details to create a new account
        </p>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit, onFormValidationError)} className="space-y-4">
        <FormItem>
          <Label htmlFor="name">Full Name</Label>
          <Input
            id="name"
            type="text"
            placeholder="Enter your full name"
            {...form.register("name")}
            disabled={isLoading}
          />
          <FormMessage>{form.formState.errors.name?.message}</FormMessage>
        </FormItem>

        <FormItem>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            {...form.register("email")}
            disabled={isLoading}
          />
          <FormMessage>{form.formState.errors.email?.message}</FormMessage>
        </FormItem>

        <FormItem>
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            placeholder="Create a password"
            {...form.register("password")}
            disabled={isLoading}
          />
          <FormMessage>{form.formState.errors.password?.message}</FormMessage>
        </FormItem>

        <FormItem>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <PasswordInput
            id="confirmPassword"
            placeholder="Confirm your password"
            {...form.register("confirmPassword")}
            disabled={isLoading}
          />
          <FormMessage>
            {form.formState.errors.confirmPassword?.message}
          </FormMessage>
        </FormItem>

        {error && <FormMessage>{error}</FormMessage>}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Creating account..." : "Create account"}
        </Button>

        <div className="text-center text-sm">
          <p className="text-muted-foreground">
            Already have an account?{" "}
            <Link
              to={withReturnUrl("/signin", searchParams)}
              className="text-primary hover:text-primary/80 underline-offset-4 hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
}
